import classNames from 'classnames';
import React, { useCallback } from 'react';
import UDPerPage from 'ud-ui/components/per-page';
import UDSearchInput from 'ud-ui/components/search';

export interface UDPagedListToolbarProps {
  /** Что набрано в поле прямо сейчас (черновик, до дебаунса). */
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Ввод ещё не доехал до запроса: лупа сменяется спиннером. */
  isSearchLoading?: boolean;
  /**
   * Размер страницы. Необязателен: список без пагинации (`UDEntityList`) его не
   * передаёт, и тогда «На странице» не рисуется вовсе.
   */
  perPage?: number;
  onPerPageChange?: (perPage: number) => void;
  /** Дополнительные фильтры страницы — год, спорт, сортировка. */
  extraFilters?: React.ReactNode;
  /** Кнопки действий над списком — «Добавить …», «Создать …». Рисуются справа. */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * Верхняя панель списка: поиск, фильтры страницы, действия, размер страницы.
 *
 * Индикатор загрузки живёт **в поле поиска**, а не поверх выдачи: так видно,
 * что запрос вызван вводом, и список при этом остаётся на экране.
 *
 * Слева — то, что сужает выдачу (поиск, фильтры), справа — то, что её меняет или
 * настраивает (кнопки, размер страницы). Поэтому `actions` и `extraFilters` не
 * взаимозаменяемы: кнопка «Добавить игрока» рядом с фильтрами читается как фильтр.
 */
const UDPagedListToolbar = ({
  search,
  onSearchChange,
  searchPlaceholder,
  isSearchLoading,
  perPage,
  onPerPageChange,
  extraFilters,
  actions,
  className,
}: UDPagedListToolbarProps) => {
  const handleChange = useCallback(
    (event: React.FocusEvent<HTMLInputElement, Element>) => onSearchChange(event.target.value),
    [onSearchChange],
  );

  const hasPerPage = perPage !== undefined && onPerPageChange !== undefined;

  return (
    <div className={classNames('mb-4 flex flex-wrap items-end gap-4', className)}>
      <UDSearchInput value={search} placeholder={searchPlaceholder} loading={isSearchLoading} onChange={handleChange} width="320px" />

      {extraFilters}

      {(actions || hasPerPage) && (
        <div className="ml-auto flex flex-wrap items-end gap-4">
          {actions}
          {hasPerPage && <UDPerPage value={perPage} onChange={onPerPageChange} />}
        </div>
      )}
    </div>
  );
};

export default UDPagedListToolbar;
